import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { ROUTES } from "@/shared/config";
import {
  formatPercent,
  formatPrice,
  markSharedElement,
  moneySign,
  percentTone,
} from "@/shared/lib";
import { Icon } from "@/shared/ui";

import { asMarket, asRiskLevel, robotColor, robotTransitionName, type RobotCard } from "../model/types";

import { AssetChips } from "./AssetChips";
import { OfferBadgeChip } from "./OfferBadgeChip";
import { OfferProgress } from "./OfferProgress";
import { RiskDot } from "./RiskDot";
import { RobotAvatar } from "./RobotAvatar";

/**
 * Строка каталога. Figma: `Robot · Anchor` (20:13) — рамка и акценты в цвете
 * робота, справа рынок капслоком.
 *
 * Переработана под ТЗ §23. Смысл правки — карточка обязана отвечать не «сколько»,
 * а «чем этот робот отличается от соседнего», поэтому вместо трёх цифр в ряд
 * появились: строка «SPOT · LOW RISK» с точкой риска, крупный результат с
 * подписью периода, токены и строка про AI-анализ.
 *
 * Главная цифра — за 30 ДНЕЙ, а не за год. Процент без периода рядом ничего не
 * значит, а год на витрине читается обещанием: месяц — это то, что уже
 * случилось и что можно проверить графиком внутри. Год остался вторичной
 * метрикой мельче. Больше двух периодов здесь не показываем: четыре процента
 * в строке высотой 122 пикселя сравнивать нечем, для лесенки есть экран робота.
 *
 * Цифры посчитаны из витринного ряда и совпадают с экраном робота до копейки —
 * это один и тот же `performance` из одного расчёта на бэкенде.
 *
 * Движение (FE-13): по нажатию строка становится общим элементом перехода и
 * перетекает в карточку на экране робота.
 *
 * Лимитированное предложение (доработки §2.2) добавляет ярлык у имени и шкалу
 * заполненности с обратным отсчётом. Карточка остаётся кликабельной во всех
 * состояниях кампании — гаснет не она, а кнопка запуска на экране робота: §2.2
 * оставляет закончившееся предложение в истории пользователя.
 *
 * Порядок в списке эта карточка не определяет: закрепление наверху считает
 * бэкенд, и сортировать список на клиенте нельзя — это второе место, где
 * описано «предложение ещё принимает деньги».
 */
export function RobotListItem({ robot }: { robot: RobotCard }) {
  const { t } = useTranslation();
  const color = robotColor(robot);
  const ref = useRef<HTMLAnchorElement>(null);

  const { active_users_count: users, total_realized_user_pnl: pnl } = robot.social_stats;
  const offer = robot.limited;

  return (
    <Link
      ref={ref}
      to={ROUTES.robot(robot.id)}
      onClick={() => {
        markSharedElement(ref.current, robotTransitionName(robot.id));
      }}
      className={`press-row flex flex-col gap-2.5 rounded-[20px] bg-background-elevated p-[14px] ${
        offer?.accepts_entries ? "border-2" : "border"
      }`}
      style={{ borderColor: color }}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="flex min-w-0 items-start gap-[9px]">
          <RobotAvatar robot={robot} size={28} className="mt-0.5" />
          <span className="flex min-w-0 flex-col gap-1">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate text-body font-semibold text-text-primary">
                {robot.name}
              </span>
              {offer ? <OfferBadgeChip offer={offer} /> : null}
            </span>
            {/* «SPOT · LOW RISK» одной строкой — как в ТЗ §23. Две отдельные
                подписи разводили бы взгляд по карточке дважды. */}
            <span className="flex items-center gap-1.5 text-micro font-semibold uppercase text-text-tertiary">
              <RiskDot risk={robot.risk_level} />
              {t(`robots.market.${asMarket(robot.market)}`)}
              <span aria-hidden="true">·</span>
              {t(`robots.riskShort.${asRiskLevel(robot.risk_level)}`)}
            </span>
          </span>
        </span>

        {/* В каталоге — ТОЛЬКО годовая цифра, и это не упрощение вёрстки.
            Карточки читаются подряд, то есть сравниваются, а сравнение
            обязано давать лесенку «дороже — доходнее» (требование заказчика).
            Годовое окно её держит: параметры в `seed.py` подобраны так, что
            порядок не ломается ни разу за 366 дат вперёд. Месячное окно её не
            держит НИКОГДА: разброс за 30 дней сопоставим с расстоянием между
            тирами, и порядок там неверен примерно в половине дней при любой
            калибровке — выпрямить его можно только доходностью порядка 50% в
            месяц у верхнего робота.

            Число никуда не делось: «Доходность за 30 дней» стоит на экране
            робота, где рядом график и подписан период. Там оно отвечает на
            вопрос «как у него дела сейчас», а не участвует в сравнении пяти
            карточек подряд. */}
        <span className="flex shrink-0 flex-col items-end gap-[3px]">
          <span className={`text-h3 font-semibold ${percentTone(robot.performance.m12)}`}>
            {formatPercent(robot.performance.m12, { sign: "always" })}
          </span>
          <span className="text-micro font-medium text-text-tertiary">{t("robots.perf.m12")}</span>
        </span>
      </div>

      <AssetChips assets={robot.assets} max={5} />

      {offer ? <OfferProgress offer={offer} /> : null}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-micro font-medium text-text-tertiary">
        {/* Порог входа — первым в строке (ТЗ §2). Это единственная цифра
            карточки, которая отвечает «могу ли я вообще его запустить», и до
            неё пользователь не должен доходить через экран запуска. */}
        <span className="font-semibold text-text-secondary">
          {t("robots.minAmount", {
            amount: formatPrice(robot.min_amount, { suffix: "USDT" }),
          })}
        </span>
        {/* Строка про AI появляется только у робота, которому оператор завёл
            набор сигналов. «AI Market Analysis» рядом с пустым блоком §13 на
            экране робота — обещание, которого экран не выполнит. */}
        {robot.ai_signals.length > 0 ? (
          <span className="flex items-center gap-1.5">
            <Icon name="sparkle" size={12} />
            {t("robots.aiAnalysis")}
          </span>
        ) : null}
        {/* Ставка показана только у тех, у кого она есть: «Комиссия 0%» на
            дешёвом роботе была бы шумом, а её отсутствие рядом с 15% у
            соседа — как раз аргумент. */}
        {moneySign(robot.profit_fee_pct) > 0 ? (
          <span className="font-semibold">
            {t("robots.fee", { pct: formatPercent(robot.profit_fee_pct) })}
          </span>
        ) : null}
      </div>

      {/* Социальное доказательство: ниже доходности и выше CTA (ТЗ №3).
          `flex-wrap` вместо сокращённого формата — на узком экране строка
          переносится, и полное число остаётся полным. Точка-разделитель
          прячется вместе со второй половиной, иначе строка кончалась бы ею.
          Оба показателя `null` — строки нет вовсе: ноль здесь означал бы
          «роботом никто не пользуется», а не «мы пока не знаем».

          Существительное зависит от знака: «−79,30 USDT прибыли» — не
          пессимистичная формулировка, а грамматическая ошибка. Знак у числа
          при этом остаётся: слово его дублирует, но не заменяет. */}
      {users !== null || pnl !== null ? (
        <span className="flex flex-wrap items-center gap-x-1.5 text-caption font-medium text-text-secondary">
          {users !== null ? <span>{t("robots.social.users", { count: users })}</span> : null}
          {users !== null && pnl !== null ? <span aria-hidden="true">·</span> : null}
          {pnl !== null ? (
            <span>
              <span className={`font-semibold ${percentTone(pnl)}`}>
                {formatPrice(pnl, { sign: "always", suffix: robot.social_stats.pnl_currency })}
              </span>{" "}
              {t(
                moneySign(pnl) < 0 ? "robots.social.lossSuffix" : "robots.social.profitSuffix",
              )}
            </span>
          ) : null}
        </span>
      ) : null}

      <span className="text-caption font-semibold" style={{ color }}>
        {t("robots.openAnalytics")} →
      </span>
    </Link>
  );
}
